/**
 * Scope: Must / If time allows / next cycle, then housekeeping, the work
 * running alongside, and the notes for later. A card moves by dragging its
 * grip or by its Move buttons; the buttons are the keyboard's way.
 */

import { useEffect, useState, type ReactElement, type ReactNode } from 'react'
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { Badge, Button, Caption1, Input, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import {
  ArrowRight12Regular,
  CalendarLtr20Regular,
  Clock20Regular,
  Dismiss12Regular,
  Flag20Regular,
  Person16Regular,
  PeopleTeam16Regular,
  Wrench20Regular,
  Add16Regular,
  Lightbulb16Regular,
  ChevronDown16Regular,
  ChevronUp16Regular,
  ReOrderDotsVertical16Regular,
} from '@fluentui/react-icons'
import { COL_KEYS, cardId, cardsIn, colName, isHouse, whoOf } from '../../lib/planning'
import type { ColKey, Plan, PlanEdit, PlanScopeItem, PlanState } from '../../types/planning'
import { IssueNote } from './IssueNote'
import { CommitInput, IssueLink, PlanChip, usePanelStyles } from './PlanParts'

const COL_ICON: Record<ColKey, ReactElement> = {
  must: <Flag20Regular />,
  maybe: <Clock20Regular />,
  later: <CalendarLtr20Regular />,
}
const SHORT: Record<ColKey, string> = { must: 'Must', maybe: 'If time', later: '' }
// Each column keeps one colour, on its top edge, icon and count, so the
// three read apart on a projector: Must the one warm colour (#1578).
const COUNT: Record<ColKey, 'severe' | 'brand' | 'informative'> = {
  must: 'severe',
  maybe: 'brand',
  later: 'informative',
}
const KIND: Record<string, string> = { users: 'Users', platform: 'Platform' }

const useStyles = makeStyles({
  cols: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: tokens.spacingHorizontalM,
    alignItems: 'start',
  },
  col: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalS,
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    borderTop: '3px solid',
    minWidth: 0,
  },
  // A card dragged over the column.
  over: { background: tokens.colorBrandBackground2, outline: `2px dashed ${tokens.colorBrandStroke1}` },
  must: { borderTopColor: tokens.colorPaletteDarkOrangeBorderActive },
  maybe: { borderTopColor: tokens.colorBrandBackground },
  later: { borderTopColor: tokens.colorNeutralStrokeAccessible },
  colHead: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontWeight: tokens.fontWeightSemibold,
  },
  mustIcon: { display: 'inline-flex', color: tokens.colorPaletteDarkOrangeForeground1 },
  maybeIcon: { display: 'inline-flex', color: tokens.colorBrandForeground1 },
  laterIcon: { display: 'inline-flex', color: tokens.colorNeutralForeground3 },
  count: { marginLeft: 'auto' },
  // An empty column still looks like a place a card can go, not a page
  // that hasn't loaded.
  none: {
    padding: tokens.spacingVerticalM,
    border: `1px dashed ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: tokens.spacingHorizontalS,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  dragging: { position: 'relative', zIndex: 3, boxShadow: tokens.shadow16 },
  top: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  // Pointer only: the Move buttons are the keyboard's way, so the grip stays
  // out of the tab order.
  grip: {
    display: 'inline-flex',
    marginLeft: 'auto',
    padding: '2px',
    color: tokens.colorNeutralForeground3,
    cursor: 'grab',
    touchAction: 'none',
    ':hover': { color: tokens.colorNeutralForeground1 },
  },
  // 12 px, sentence case: 10 px capitals could not be read projected (#1578).
  kind: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  title: { margin: 0, fontWeight: tokens.fontWeightSemibold },
  foot: { display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' },
  who: { flexGrow: 1, minWidth: '120px' },
  side: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  row: { display: 'flex', flexDirection: 'column', gap: '4px' },
  rowHead: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  rowBody: { paddingLeft: tokens.spacingHorizontalL },
  parkForm: { display: 'flex', gap: tokens.spacingHorizontalS },
  parkItem: { display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'space-between' },
})

function Column({ k, className, children }: { k: ColKey; className: string; children: ReactNode }) {
  const styles = useStyles()
  const { setNodeRef, isOver } = useDroppable({ id: k })
  return (
    <div
      ref={setNodeRef}
      className={mergeClasses(className, isOver && styles.over)}
      data-testid={`col-${k}`}
    >
      {children}
    </div>
  )
}

/** A card that drags by its grip; `children` says where the grip goes. */
function DragCard({
  id,
  here,
  children,
}: {
  id: string
  here: ColKey
  children: (grip: ReactNode) => ReactNode
}) {
  const styles = useStyles()
  const { setNodeRef, listeners, transform, isDragging } = useDraggable({ id, data: { from: here } })
  return (
    <article
      ref={setNodeRef}
      className={mergeClasses(styles.card, isDragging && styles.dragging)}
      style={{ transform: CSS.Translate.toString(transform) }}
      data-testid={`card-${id}`}
      tabIndex={-1}
      aria-labelledby={`card-title-${id}`}
    >
      {children(
        <span className={styles.grip} aria-hidden="true" data-testid={`grip-${id}`} {...listeners}>
          <ReOrderDotsVertical16Regular />
        </span>,
      )}
    </article>
  )
}

export function ScopeBoard({
  plan,
  state,
  edit,
  cycle = plan.cycle,
  columns = true,
}: {
  plan: Plan
  state: PlanState
  edit: (e: PlanEdit) => void
  /** The stored cycle: an imported file may leave its own `cycle` out. */
  cycle?: string
  /** False under the map, which draws the cards: only the notes for later stay. */
  columns?: boolean
}) {
  const styles = useStyles()
  const panel = usePanelStyles()
  const [park, setPark] = useState('')
  // The card just moved: its Move button is gone with the old column, so the
  // keyboard follows the card to the new one instead of falling to the page (#1578).
  const [moved, setMoved] = useState<{ id: string; to: ColKey } | null>(null)
  useEffect(() => {
    if (!moved) return
    const card = document.querySelector<HTMLElement>(
      `[data-testid="col-${moved.to}"] [data-testid="card-${moved.id}"]`,
    )
    if (!card) return // the room's state has not caught up with the move yet
    card.focus()
    setMoved(null)
  }, [moved, state])
  const scope = plan.scope ?? []
  const house = scope.filter(isHouse)
  const along = scope.filter((c) => c.col === 'along' && !isHouse(c))
  const parking = state.parking ?? []
  // Rows outside the columns opened to their summary and comment box.
  const [shown, setShown] = useState<string[]>([])
  // One per issue: a card can carry several (Copilot on #1618).
  const note = (c: PlanScopeItem) =>
    c.refs.map((n) => (
      <IssueNote
        key={n}
        cycle={cycle}
        issue={n}
        title={c.refs.length > 1 ? `${c.t} (#${n})` : c.t}
        sum={plan.sums?.[String(n)]?.s}
        numbered={c.refs.length > 1}
      />
    ))
  const row = (c: PlanScopeItem) => {
    const id = cardId(c)
    const open = shown.includes(id)
    return (
      <li key={id} className={styles.row}>
        <div className={styles.rowHead}>
          {c.refs.map((n) => (
            <IssueLink key={n} n={n} base={plan.repo_url} />
          ))}
          <span>{c.t}</span>
          <PlanChip chip={c.tag} />
          <Button
            size="small"
            appearance="subtle"
            icon={open ? <ChevronUp16Regular /> : <ChevronDown16Regular />}
            aria-expanded={open}
            aria-label={`Details of ${c.t}`}
            onClick={() => setShown(open ? shown.filter((x) => x !== id) : [...shown, id])}
          />
        </div>
        {open && <div className={styles.rowBody}>{note(c)}</div>}
      </li>
    )
  }

  const move = (id: string, to: ColKey) => {
    edit({ op: 'set', path: ['cols', id], value: to })
    setMoved({ id, to })
  }
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    const to = over?.id as ColKey | undefined
    if (to && to !== active.data.current?.from) move(String(active.id), to)
  }
  // A card that is also an open question says so; its outcome is made in Decisions.
  const asked = new Set((plan.questions ?? []).map((q) => q.id))
  const question = (c: PlanScopeItem) => {
    const qs = c.refs.filter((n) => asked.has(n))
    if (!qs.length) return null
    // A card with several questions is decided only when each one is.
    const done = qs.every((n) => !!state.out?.[String(n)])
    return (
      <Badge appearance="outline" size="small" color={done ? 'success' : 'warning'}>
        {done ? 'Decided' : 'To decide'}
      </Badge>
    )
  }

  const card = (c: PlanScopeItem, here: ColKey) => {
    const id = cardId(c)
    const kind = c.kind_label || KIND[c.kind ?? '']
    return (
      <DragCard key={id} id={id} here={here}>
        {(grip) => (
          <>
            <div className={styles.top}>
              {c.refs.map((n) => (
                <IssueLink key={n} n={n} base={plan.repo_url} />
              ))}
              {kind && <span className={styles.kind}>{kind}</span>}
              <PlanChip chip={c.tag} />
              {question(c)}
              {grip}
            </div>
            <p className={styles.title} id={`card-title-${id}`}>
              {c.t}
            </p>
            {note(c)}
            <div className={styles.foot}>
              <CommitInput
                className={styles.who}
                size="small"
                appearance="underline"
                contentBefore={<Person16Regular aria-hidden="true" />}
                aria-label={`Owner of ${c.t}`}
                placeholder="Owner"
                value={whoOf(c, state)}
                onCommit={(v) => edit({ op: 'set', path: ['who', id], value: v })}
              />
              {COL_KEYS.filter((k) => k !== here).map((k) => (
                <Button
                  key={k}
                  size="small"
                  appearance="subtle"
                  icon={<ArrowRight12Regular />}
                  aria-label={`Move ${c.t} to ${colName(plan, k)}`}
                  onClick={() => move(id, k)}
                >
                  {SHORT[k] || colName(plan, k)}
                </Button>
              ))}
            </div>
          </>
        )}
      </DragCard>
    )
  }

  return (
    <>
      {columns && plan.ai === 'off' && (
        <Caption1 className={panel.muted}>AI summaries are off on this server.</Caption1>
      )}
      {columns && (
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className={styles.cols}>
          {COL_KEYS.map((k) => {
            const cards = cardsIn(plan, state, k)
            return (
              <Column key={k} k={k} className={mergeClasses(styles.col, styles[k])}>
                <div className={styles.colHead}>
                  <span className={styles[`${k}Icon` as const]} aria-hidden="true">
                    {COL_ICON[k]}
                  </span>
                  <span>{colName(plan, k)}</span>
                  <Badge
                    className={styles.count}
                    appearance="tint"
                    shape="rounded"
                    color={COUNT[k]}
                  >
                    {cards.length}
                  </Badge>
                </div>
                {plan.cols?.[k] && <Caption1 className={panel.muted}>{plan.cols[k]}</Caption1>}
                {cards.length === 0 && <Caption1 className={styles.none}>Nothing yet</Caption1>}
                {cards.map((c) => card(c, k))}
              </Column>
            )
          })}
        </div>
      </DndContext>
      )}

      {columns && house.length > 0 && (
        <div className={panel.panel}>
          <div className={panel.panelHead}>
            <Wrench20Regular aria-hidden="true" />
            Housekeeping · {house.length}
          </div>
          <Caption1 className={panel.muted}>
            {plan.house_note || 'As time allows; no discussion unless someone asks.'}
          </Caption1>
          <ul className={panel.list}>{house.map(row)}</ul>
        </div>
      )}

      <div className={styles.side}>
        {columns && along.length > 0 && (
          <div className={panel.panel}>
            <div className={panel.panelHead}>
              <PeopleTeam16Regular aria-hidden="true" />
              {plan.along_title || 'Running alongside'}
            </div>
            <ul className={panel.list}>{along.map(row)}</ul>
          </div>
        )}
        <div className={panel.panel}>
          <div className={panel.panelHead}>
            <Lightbulb16Regular aria-hidden="true" />
            Notes for later
          </div>
          {parking.length === 0 ? (
            <Caption1 className={panel.muted}>Empty.</Caption1>
          ) : (
            <ul className={panel.list}>
              {parking.map((p) => (
                <li key={p} className={styles.parkItem}>
                  <span>{p}</span>
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<Dismiss12Regular />}
                    aria-label={`Remove ${p}`}
                    onClick={() => edit({ op: 'remove', path: ['parking'], value: p })}
                  />
                </li>
              ))}
            </ul>
          )}
          <form
            className={styles.parkForm}
            onSubmit={(e) => {
              e.preventDefault()
              if (!park.trim()) return
              edit({ op: 'add', path: ['parking'], value: park.trim() })
              setPark('')
            }}
          >
            <Input
              size="small"
              value={park}
              onChange={(_, d) => setPark(d.value)}
              placeholder="New ask: note it here"
              aria-label="Add to notes for later"
              style={{ flexGrow: 1 }}
            />
            <Button size="small" type="submit" icon={<Add16Regular />} disabled={!park.trim()}>
              Add
            </Button>
          </form>
        </div>
      </div>
    </>
  )
}
